import type { LocationQuery, RouteLocationNormalized } from 'vue-router'

/**
 * Url Parsing
 */

export const queryPathRegex = /\/query\/([0-9.-]+,[0-9.-]+)/

/**
 * Extract numeric lat/lon from a path matching /query/{lat},{lon}
 * If not present, returns undefined
 *
 * @returns [lon, lat]
 */
export const queryFromPath = (path: string) => {
  const coords = (path.match(queryPathRegex) ?? [])[1]?.split(',')
  if (coords?.length) {
    const [lat, lon] = [parseFloat(coords[0]), parseFloat(coords[1])]
    if (typeof lon === 'number' && typeof lat === 'number') {
      return [lon, lat] satisfies [number, number]
    }
  }
}

/**
 * Extract numeric lat/lon from query params
 * if not present, returns undefined
 *
 * @returns [lon, lat]
 */
export const queryFromParam = (route: RouteLocationNormalized) => {
  if (route.query.lat && route.query.lon) {
    if (
      typeof route.query.lat === 'string' &&
      typeof route.query.lon === 'string'
    ) {
      const [lon, lat] = [
        parseFloat(route.query.lon),
        parseFloat(route.query.lat),
      ]
      if (!isNaN(lon) && !isNaN(lat)) {
        return [lon, lat] satisfies [number, number]
      }
    }
  }
}

export const parseQueryCoordinates = (route: RouteLocationNormalized) => {
  return queryFromPath(route.fullPath) ?? queryFromParam(route)
}

export const parseParams = (queryObj: LocationQuery) => {
  const cleanQuery = Object.fromEntries(
    Object.entries(queryObj).filter(([, v]) => typeof v === 'string') as [
      string,
      string,
    ][]
  )
  try {
    return Object.assign(
      {},
      cleanQuery,
      typeof cleanQuery.after_login === 'string'
        ? Object.fromEntries(
            new URL(
              cleanQuery.after_login,
              window.location.origin
            ).searchParams.entries()
          )
        : {}
    )
  } catch {
    return Object.assign({}, cleanQuery)
  }
}

const MAX_BOUNDS_PADDING = 0.75

export const calculateMaxBounds = (
  coordinates: [number, number]
): [number, number, number, number] => {
  return [
    coordinates[0] - MAX_BOUNDS_PADDING,
    coordinates[1] - MAX_BOUNDS_PADDING,
    coordinates[0] + MAX_BOUNDS_PADDING,
    coordinates[1] + MAX_BOUNDS_PADDING,
  ]
}
